// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Canonical action-icon vocabulary for record-list row actions and the
 * RecordActionsDrawer. One glyph per action verb, shared by every module so
 * an action always looks the same wherever it appears (row toolbar → drawer).
 *
 * Feather/Lucide-style single-colour stroke icons that inherit `currentColor`
 * and size, matching the drawer close button already shipped. Presentational
 * only — every icon is `aria-hidden`; the accessible name comes from the
 * button that wraps it (see RowActionIconButton / AdvisorRunButton).
 */

import type { SVGProps } from 'react';

export type ActionIconProps = SVGProps<SVGSVGElement> & { size?: number };

function Base({ size = 18, children, ...rest }: ActionIconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Ask Forseti — AI advisor. Always rendered with a brand tint. */
export function ForsetiIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5 10.1 7.6z" />
      <path d="M18.5 15.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
    </Base>
  );
}

/** Edit — open the record form. */
export function EditIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </Base>
  );
}

/** Copy / clone — create a new record from an existing one. */
export function CopyIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M15 9V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h4" />
    </Base>
  );
}

/** Submit for review — advance a draft. */
export function SubmitIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4z" />
    </Base>
  );
}

/** Approve / reject — a decision on a review item (balance/scales). */
export function DecisionIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M12 3v18" />
      <path d="M5 7l-3 6a4 4 0 0 0 6 0z" />
      <path d="M19 7l3 6a4 4 0 0 1-6 0z" />
      <path d="M7 7h10" />
      <path d="M8 21h8" />
    </Base>
  );
}

/** More actions — opens the record actions drawer (horizontal kebab). */
export function MoreIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base strokeWidth={1.9} {...props}>
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </Base>
  );
}

/** Risk evaluation — score / re-score risk (gauge). */
export function RiskIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M12 21a9 9 0 1 0-9-9" />
      <path d="M12 12l4-2.5" />
      <path d="M3 12h2" />
      <path d="M6 6l1.4 1.4" />
    </Base>
  );
}

/** Mechanism proof / attachments (paperclip). */
export function ProofIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M21.4 11.05l-8.5 8.5a5 5 0 0 1-7.07-7.07l8.49-8.49a3.34 3.34 0 0 1 4.72 4.72l-8.5 8.49a1.67 1.67 0 1 1-2.36-2.36l7.79-7.78" />
    </Base>
  );
}

/** Safeguards — controls sub-list (shield-check). */
export function SafeguardIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </Base>
  );
}

/** File dossier — start a regulatory filing (folder+). */
export function DossierIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      <path d="M12 11v6" />
      <path d="M9 14h6" />
    </Base>
  );
}

/** Deprecate / archive — retire without deleting. */
export function ArchiveIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <path d="M10 12h4" />
    </Base>
  );
}

/** Delete — destructive (trash). */
export function TrashIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </Base>
  );
}

/** View — read-only detail (eye). */
export function ViewIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </Base>
  );
}

/** Generate / export — PDF, DOCX, CSV output (file-down). */
export function ExportIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M12 18v-6" />
      <path d="M9.5 15.5L12 18l2.5-2.5" />
    </Base>
  );
}

/** Chevron — affordance that a drawer action row opens further UI. */
export function ChevronRightIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base strokeWidth={1.8} {...props}>
      <path d="M9 18l6-6-6-6" />
    </Base>
  );
}

/** Activate / re-activate — bring a record into effect (play). */
export function ActivateIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5l5 3.5-5 3.5z" />
    </Base>
  );
}

/** Suspend — temporarily pause a record (pause). */
export function SuspendIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 9v6M14 9v6" />
    </Base>
  );
}

/** Reject — decline a review item (x-circle, danger). */
export function RejectIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M15 9l-6 6M9 9l6 6" />
    </Base>
  );
}

/** Flag — mark as sensitive / for attention. */
export function FlagIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V4s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <path d="M4 22v-7" />
    </Base>
  );
}

/** Accept / approve — a positive decision (check-circle). */
export function CheckCircleIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12l2.5 2.5 4.5-5" />
    </Base>
  );
}

/** Delegate / assign / route to an owner (user-plus). */
export function AssignIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6M22 11h-6" />
    </Base>
  );
}

/** Merge two records into one (git-merge). */
export function MergeIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="9" r="3" />
      <path d="M6 9v6" />
      <path d="M15 9a9 9 0 0 1-9 9" />
    </Base>
  );
}

/** Promote — advance a record forward/up (arrow-up-circle). */
export function PromoteIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16V8M8.5 11.5L12 8l3.5 3.5" />
    </Base>
  );
}

/** Restore — bring a soft-deleted record back (counter-clockwise undo). */
export function RestoreIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M3 3v6h6" />
      <path d="M3.5 9a9 9 0 1 1-1.4 5" />
    </Base>
  );
}

/** Escalate / enhanced review — heightened scrutiny (shield-alert). */
export function ShieldAlertIcon(props: ActionIconProps): JSX.Element {
  return (
    <Base {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M12 8v4" />
      <path d="M12 15.5v.5" />
    </Base>
  );
}
